import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { EmptyState } from '../ui/EmptyState';
import { Button } from '../ui/Button';
import { useDragSort } from '../ui/useDragSort';
import type { Container } from '../db/board';
import { reorderEntries } from '../db/sections';
import { useBoardFilter } from '../search/searchStore';
import { useExport } from '../settings/useExport';
import { useBoardData, useBoardOrder, coverOf } from './useBoardData';
import { useBoardMoves } from './useBoardMoves';
import { useBoardLayout } from './useBoardLayout';
import { useBoardKeys } from './useBoardKeys';
import { useSelection } from './useSelection';
import { useContainerWidth } from './useContainerWidth';
import { useFileIntake } from './useFileIntake';
import { BoardHeader } from './BoardHeader';
import { BoardGrid } from './BoardGrid';
import { BoardNotices } from './BoardNotices';
import { SelectionBar } from './SelectionBar';
import { BoardDialogs, type BoardDialog } from './BoardDialogs';
import { parentNameOf, sectionPath } from './paths';
import { boardOutcomes } from './outcomes';
import './Board.css';

/** Long enough for the heading's blink to finish. Matches `crumb-blink`. */
const BLINK_MS = 800;

/** Pinterest inside the grid, Living Spaces around it. */
export function Board() {
  const { roomId, sectionId, itemId } = useParams();
  const navigate = useNavigate();

  const board = useRef<HTMLDivElement>(null);
  const [measureRef, width] = useContainerWidth();

  // The width observer takes a callback ref; revealing a tile needs the node
  // itself, so the two share one.
  const grid = useRef<HTMLElement | null>(null);
  const gridRef = useCallback(
    (node: HTMLElement | null) => {
      grid.current = node;
      measureRef(node);
    },
    [measureRef],
  );

  const [dialog, setDialog] = useState<BoardDialog | undefined>(undefined);

  // Counts arrivals rather than holding a flag: spring twice into the same
  // heading and the animation has to start over, which it won't if the class
  // never leaves.
  const [arrived, setArrived] = useState(0);

  // And it has to stop meaning anything once it's played, or every later
  // heading that happens to mount would blink at nothing.
  useEffect(() => {
    if (arrived === 0) return;
    const timer = window.setTimeout(() => setArrived(0), BLINK_MS);
    return () => window.clearTimeout(timer);
  }, [arrived]);

  const container = useMemo<Container>(
    () => ({ roomId: roomId ?? '', sectionId }),
    [roomId, sectionId],
  );

  // A search scoped to this room. While one is on, the board is a flat wall of
  // matches from anywhere in the room rather than the container you're in.
  const filter = useBoardFilter();

  const source = useBoardData(roomId, sectionId, filter);
  const { data, entries, ids, byId } = source;

  const moves = useBoardMoves({
    container,
    byId,
    pathOf: useCallback((section) => sectionPath(roomId ?? '', section), [roomId]),
    onArrive: useCallback(() => setArrived((count) => count + 1), []),
  });

  const sortable = useDragSort({
    ids,
    // Rearranging a filtered view would write an order for a board nobody is
    // looking at.
    enabled: dialog === undefined && filter === undefined,
    onCommit: (order) => void reorderEntries(order),
    onMerge: (draggedId, targetId) => void moves.group(draggedId, targetId),
    onSpring: (draggedId, targetId) => void moves.spring(draggedId, targetId),
  });

  const { ordered, sized } = useBoardOrder(source, sortable.order);

  // Items only, in the order the eye reads them — that's what a shift-click
  // range counts across. Sections aren't selectable: moving, exporting and
  // deleting all mean something different for a container.
  const itemIds = useMemo(
    () => ordered.flatMap((entry) => (entry.kind === 'item' ? [entry.id] : [])),
    [ordered],
  );

  const selection = useSelection(itemIds);
  const exporter = useExport();

  const { placements, height, laidOut } = useBoardLayout({
    roomId,
    sectionId,
    sized,
    width,
    itemIds,
    openItemId: itemId,
    grid,
  });

  useBoardKeys({
    enabled: dialog === undefined,
    hasSelection: selection.count > 0,
    onAdd: useCallback(() => setDialog({ kind: 'add-item' }), []),
    onDeleteSelection: useCallback(
      () => setDialog({ kind: 'delete-items', itemIds: selection.ids }),
      [selection.ids],
    ),
    onClearSelection: selection.clear,
  });

  // A selection belongs to the container it was made in. Walking into a
  // section with three tiles still highlighted somewhere behind you is how a
  // delete lands on the wrong things.
  const clearSelection = selection.clear;
  useEffect(() => clearSelection, [roomId, sectionId, clearSelection]);

  const intake = useFileIntake({
    container: roomId === undefined ? undefined : container,
    // A paste with the panel open belongs to the open item, not to a new one.
    enabled: dialog === undefined && itemId === undefined,
    // New items go to the front, which is off-screen if you've scrolled.
    onCreated: () => board.current?.scrollIntoView({ block: 'start', behavior: 'smooth' }),
  });

  if (data === undefined) {
    return <div className="board" aria-busy="true" />;
  }

  const room = data.room;
  if (room === undefined) {
    return (
      <div className="board">
        <EmptyState
          title="Room Not Found"
          line="It may have been deleted, or the link is out of date."
        />
      </div>
    );
  }

  const view = data.view;
  if (sectionId !== undefined && view.section === undefined) {
    return (
      <div className="board">
        <EmptyState
          title="Section Not Found"
          line="It may have been deleted, or the link is out of date."
          action={<Button onClick={() => void navigate(`/room/${room.id}`)}>Back To {room.name}</Button>}
        />
      </div>
    );
  }

  // The dragged thing's cover leads the preview: it's the newest thing in
  // whatever it's about to become part of.
  const dragged = sortable.draggingId === undefined ? undefined : byId.get(sortable.draggingId);
  const merged = sortable.mergeId === undefined ? undefined : byId.get(sortable.mergeId);
  const mergeCovers = [
    ...(dragged === undefined ? [] : coverOf(dragged)),
    ...(merged === undefined ? [] : coverOf(merged)),
  ];

  return (
    <div className="board" ref={board} {...intake.handlers}>
      <BoardHeader
        room={room}
        path={view.path}
        // A filtered board is the whole room flattened, so what it's a
        // fraction of is the room and not the container.
        count={filter === undefined ? view.count : view.roomCount}
        matching={filter === undefined ? undefined : entries.length}
        dragging={sortable.draggingId !== undefined}
        holdingId={sortable.dropId}
        arrived={arrived}
        onAddItem={() => setDialog({ kind: 'add-item' })}
        onAddSection={() => setDialog({ kind: 'add-section' })}
        onEditSection={() =>
          view.section !== undefined && setDialog({ kind: 'edit-section', section: view.section })
        }
        onDeleteSection={() =>
          view.section !== undefined && setDialog({ kind: 'delete-section', section: view.section })
        }
      />

      <BoardNotices intake={intake} exporter={exporter} />

      {selection.count > 0 && (
        <SelectionBar
          selection={selection}
          exporting={exporter.busy}
          onMove={() => setDialog({ kind: 'move', itemIds: selection.ids })}
          onExport={() => void exporter.run(selection.ids)}
          onDelete={() => setDialog({ kind: 'delete-items', itemIds: selection.ids })}
        />
      )}

      {entries.length === 0 &&
        intake.adding === 0 &&
        (filter === undefined ? (
          <EmptyState
            title="Nothing Here Yet"
            line="Add something you've bought, or drop photos straight onto this page."
            action={<Button onClick={() => setDialog({ kind: 'add-item' })}>Add Item</Button>}
          />
        ) : (
          <EmptyState title="Nothing Matches" line={`Nothing in ${room.name} mentions that.`} />
        ))}

      <BoardGrid
        entries={ordered}
        placements={placements}
        roomId={room.id}
        base={sectionPath(room.id, sectionId)}
        openItemId={itemId}
        highlight={filter}
        animate={laidOut}
        sortable={sortable}
        selection={selection}
        mergeCovers={mergeCovers}
        gridRef={gridRef}
        height={height}
        onMoveItem={(item) => setDialog({ kind: 'move', itemIds: [item.id] })}
        onDeleteItem={(item) => setDialog({ kind: 'delete-item', item })}
        onEditSection={(section) => setDialog({ kind: 'edit-section', section })}
        onDeleteSection={(section) => setDialog({ kind: 'delete-section', section })}
      />

      {intake.dragging && (
        <div className="board__drop" aria-hidden="true">
          <span className="board__drop-label">Drop Photos To Add Them</span>
        </div>
      )}

      <BoardDialogs
        open={dialog}
        container={container}
        parentNameOf={(section) => parentNameOf(section, room, view.path)}
        onClose={() => setDialog(undefined)}
        {...boardOutcomes({
          navigate: (to) => void navigate(to),
          room,
          sectionId,
          itemId,
          path: view.path,
          selection,
          close: () => setDialog(undefined),
        })}
      />
    </div>
  );
}
